import React from 'react';
import { useWatch } from 'react-hook-form';
import __t from '../../lib/traduzioni';
import { FormGroup, Box } from '@mui/material';
import BSSText from '../form/BSSText';
import useBSSLogger from '../../hooks/useBSSLogger';
import { luogoDaCodiceFiscale } from '../../api/datiintestazione';
import { useAsyncFn, useDebounce } from 'react-use';
import {parse, isValid} from 'date-fns'

const {logger} = useBSSLogger();

const mappaMesi = [
    0, 'A', 'B', 'C', 'D', 'E',
    'H', 'L', 'M', 'P', 'R',
    'S', 'T'
]

const vocali = [
    'A', 'E', 'I', 'O', 'U'
]

/**
 * in caso di codici fiscali identici i 7 caratteri numeri del codice fiscale potrebbero assumere i seguenti valori
 * al fine del calcolo della localita, data di nascita e sesso occorre comunque prendere il valore numerico originario
 * per il calcolo del codice di controllo occorre considerare il codice fiscale senza effettuare sostituzioni
 */
const sostnum = {
    "L" : 0, "M": 1, "N": 2, "P": 3, "Q": 4, "R": 5, "S": 6, "T": 7, "U": 8, "V": 9
}
    


const convpos = {
    '0': {pari: 0, dispari: 1}, 
    '1': {pari: 1, dispari: 0},
    '2': {pari: 2, dispari: 5},
    '3': {pari: 3, dispari: 7},
    '4': {pari: 4, dispari: 9},
    '5': {pari: 5, dispari: 13},
    '6': {pari: 6, dispari: 15},
    '7': {pari: 7, dispari: 17},
    '8': {pari: 8, dispari: 19},
    '9': {pari: 9, dispari: 21},
    'A': {pari: 0, dispari: 1},
    'B': {pari: 1, dispari: 0},
    'C': {pari: 2, dispari: 5},
    'D': {pari: 3, dispari: 7},
    'E': {pari: 4, dispari: 9},
    'F': {pari: 5, dispari: 13},
    'G': {pari: 6, dispari: 15},
    'H': {pari: 7, dispari: 17},
    'I': {pari: 8, dispari: 19},
    'J': {pari: 9, dispari: 21},
    'K': {pari: 10, dispari: 2},
    'L': {pari: 11, dispari: 4},
    'M': {pari: 12, dispari: 18},
    'N': {pari: 13, dispari: 20},
    'O': {pari: 14, dispari: 11},
    'P': {pari: 15, dispari: 3},
    'Q': {pari: 16, dispari: 6},
    'R': {pari: 17, dispari: 8},
    'S': {pari: 18, dispari: 12},
    'T': {pari: 19, dispari: 14},
    'U': {pari: 20, dispari: 16},
    'V': {pari: 21, dispari: 10},
    'W': {pari: 22, dispari: 22},
    'X': {pari: 23, dispari: 25},
    'Y': {pari: 24, dispari: 24},
    'Z': {pari: 25, dispari: 23},   
}

const lettereControllo = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'];

const calcolaStrutturaParola = parola => {
    return parola.toUpperCase().replace(/\s+/g, '').split("")
        .reduce((struttura, lettera) => {
        
            const voc = vocali.includes(lettera) ? 'vocali' : 'consonanti'

            return {
                ...struttura,
                [voc]: [...struttura[voc], lettera]             
            }
        
        }, {consonanti: [], vocali: []});

}


const localCheckCodiceFiscale = ({codfisc, nome, cognome, methods}) => {
    logger.debug("controllo il codice fiscale", codfisc, nome, cognome)

    let motivononvalido = {}

    codfisc = (codfisc ?? "").toUpperCase();

    let c1 = (codfisc?.[0] ?? null);
    let c2 = (codfisc?.[1] ?? null);
    let c3 = (codfisc?.[2] ?? null);
    let n1 = (codfisc?.[3] ?? null);
    let n2 = (codfisc?.[4] ?? null);
    let n3 = (codfisc?.[5] ?? null);
    let a1 = (codfisc?.[6] ?? null);
    let a2 = (codfisc?.[7] ?? null);
    let m1 = (codfisc?.[8] ?? null);
    let g1 = (codfisc?.[9] ?? null);
    let g2 = (codfisc?.[10] ?? null);
    let l1 = (codfisc?.[11] ?? null);
    let l2 = (codfisc?.[12] ?? null);
    let l3 = (codfisc?.[13] ?? null);
    let l4 = (codfisc?.[14] ?? null);
    let h1 = (codfisc?.[15] ?? null);    


    let luogonascita = 'Italia';

    if(l1 !== null){
        
        if(l1 === 'Z'){
            luogonascita = 'Estero';
        }  
    }

    if(l2 !== null && sostnum[l2]!== undefined){
        l2 = sostnum[l2]
    }      

    if(l3 !== null && sostnum[l3]!== undefined){
        l3 = sostnum[l3]
    }      

    if(l4 !== null && sostnum[l4]!== undefined){
        l4 = sostnum[l4]
    }      
    

    if(a1 !== null && sostnum[a1]!== undefined){
        a1 = sostnum[a1]
    }      

    if(a2 !== null && sostnum[a2]!== undefined){
        a2 = sostnum[a2]
    }      

    if(g1 !== null && sostnum[g1]!== undefined){
        g1 = sostnum[g1]
    } 
    
    if(g2 !== null && sostnum[g2]!== undefined){
        g2 = sostnum[g2]
    } 


    let annonascita = a1 !== null && a2 !== null ? `${a1}${a2}` : null;
    let giornonascita = g1 !== null && g2 !== null ? `${g1}${g2}` : null;
    let sesso = 'M';

    if(giornonascita !== null){
        giornonascita = Number(giornonascita);
        if(giornonascita >= 40){
            sesso = 'F';
            giornonascita -= 40;
        }
    }    

    let mesenascita = m1 !== null ? mappaMesi.indexOf(m1) : null;
    if(mesenascita !== null){
        mesenascita = (""+mesenascita).padStart(2, '0')
    }

    let datanascita = giornonascita !== null && mesenascita !== null && annonascita !== null ? `${giornonascita}-${mesenascita}-${annonascita}` : null;

    if(luogonascita !== null) {
        methods.setValue("datifatt.luogonascita", luogonascita);        
    } else {
        methods.setValue("datifatt.luogonascita", null);
    }

    if(datanascita !== null) {
        methods.setValue("datifatt.datanascita", datanascita);
        motivononvalido["datanascita"] = !isValid(parse(datanascita, "dd-MM-yy", new Date));
    } else {
        methods.setValue("datifatt.datanascita", null);
    }

    if(sesso !== null){
        methods.setValue("datifatt.sesso", sesso);
    } else {
        methods.setValue("datifatt.sesso", null);
    }

    let cognomecf = "";
    let strutturacognome = {consonanti: [], vocali: []}
    if((cognome?.length ?? 0) >= 1){
        strutturacognome = calcolaStrutturaParola(cognome)
    }
    const strcognome = `${strutturacognome.consonanti.join("")}${strutturacognome.vocali.join("")}XXX`;
    cognomecf = strcognome.substr(0, 3);
    const expcognome = `${c1}${c2}${c3}`.toUpperCase();
    logger.debug("struttura cognome", cognome, strutturacognome, strcognome, expcognome)
    motivononvalido["cognome"] = (cognomecf !== expcognome);

    let nomecf = "";
    let strutturanome = {consonanti: [], vocali: []}
    if((nome?.length ?? 0) >= 1){
        strutturanome = calcolaStrutturaParola(nome)
    }
    const [cnome1 = "", cnomeignora, cnome2 = "", ...cnomerest] = strutturanome.consonanti;
    const strnome = `${cnome1}${cnome2}${cnomerest.join("")}${strutturanome.vocali.join("")}XXX`;
    const expnome = `${n1}${n2}${n3}`.toUpperCase();
    logger.debug("struttura nome", nome, strutturanome, strnome, expnome)
    nomecf = strnome.substr(0, 3);
    motivononvalido["nome"] = (nomecf !== expnome);

    const cfiscricomposto = (codfisc ?? "").toUpperCase();
    let ccontrollo = null

    if((cfiscricomposto?.length ?? 0) === 16){
        
    
        const numcontrollo = cfiscricomposto.split("").reduce((controllo, carattere, indice) => {
            let chiave = 'pari'
            
            if((indice+1) % 2 !== 0){
                chiave = 'dispari'
            }

            const addendo = indice < 15 ? convpos[carattere][chiave] : 0;

            return controllo + addendo;

        }, 0) % 26

        ccontrollo = lettereControllo[numcontrollo];
        logger.debug("controllo codfisc", cfiscricomposto, ccontrollo)

        motivononvalido["codicecontrollo"] = (h1 !== ccontrollo); 

    }

    const motivi = Object.keys(motivononvalido).reduce((lista, motivo) => {
        if(motivononvalido[motivo]){
            lista = [...lista, motivo]
        }
        return lista;
    }, [])

    let messaggio = null;

    if(motivi.length){
        messaggio = `${__t("codicefiscale")} ${__t("nonvalido")}, ${__t("controllailvaloredi")}: ${motivi.map(motivo => __t(motivo)).join(", ")}`
    }
    

    return {
        codfisc,
        nome,
        cognome,
        luogonascita,
        sesso,
        datanascita,
        ccontrollo,
        nomecf,
        expnome,
        cognomecf,
        expcognome,
        motivi,
        messaggio,
        sesso
    };
}

/**
 * fonte: https://www.studioaleo.it/struttura-codice-fiscale.html
 * 
 * @param {*} param0 
 */
const controllaCodiceFiscale = ({codfisc, nome, cognome, methods}) => {
    
    const asyncFn = async ({codfisc, nome, cognome, methods}) => {
        try {

            const datilocali = localCheckCodiceFiscale({codfisc, nome, cognome, methods})

            logger.debug("cerco il comune a partire dal codice fiscale", codfisc, nome, cognome, datilocali)

            //const res = await luogoDaCodiceFiscale(term);
            const res = await new Promise((resolve, reject) => {
                setTimeout(()=> {
                    resolve(datilocali)
                }, 1000)
            })

            return res; 
        } catch(err){
            logger.error("problema controllo codice fiscale", err);
        }
        
    };

    const [{loading, value}, callback] = useAsyncFn(asyncFn)

    const [, cancel] = useDebounce(
        () => {
            if(((codfisc?.length ?? 0) === 16)){
                logger.debug("codice fiscale cambiato e di lunghezza valida", codfisc)
                callback({codfisc, nome, cognome, methods})
            }
        },
        500,         
        [callback, codfisc, nome, cognome]
    )

    return {
        loading,
        value
    }
    
}


const CodFisc = ({methods, boxProps={width: {xs: 1, md: 1, lg: 1}, mb: "1.2em"}}) => {

    const piva = useWatch({name: "datifatt.partitaiva"})
    const codfisc = useWatch({name: "datifatt.codicefiscale"})
    const nome = useWatch({name: "datifatt.nome"})
    const cognome = useWatch({name: "datifatt.cognome"})
    const tppers = useWatch({name: "datifatt.anagrafica.tipopersona"})

    const tipopers = tppers?.value ?? null;

    const requiredVal = ['Fisica', 'LiberoProfessionistaFisica'].includes(tipopers);
    const requiredWithoutVal = (['PA'].includes(tipopers) && [null, undefined, ""].includes(piva));
    const message = requiredVal ? 
        __t("validazione.requiredField", ["codicefiscale"]) : 
        requiredWithoutVal ? __t("validazione.requiredwithout", [[__t("partitaiva"), __t("codicefiscale")].join(", ")]) : null

    const mostra = requiredVal || ['PA'].includes(tipopers);

    

    // if(!mostra){
    //     methods.unregister(['datifatt.nome', 'datifatt.cognome', 'datifatt.codicefiscale', 'datifatt.luogonascita', 'datifatt.datanascita', 'datifatt.sesso'], {
    //         keepDirt: false, keepTouched: false, keepValid: false, keepError: false, keepValue: false, keepDefaultValue: false
    //     })
    // } 

    const {loading: loadingControllaCodiceFiscale, value: controlloCodiceFiscale} = controllaCodiceFiscale({
        codfisc, 
        nome, 
        cognome, 
        methods
    })


    const deprules = {
        required: {value: requiredVal || requiredWithoutVal, message},
        
    }
    

    return (
        <>
            {/* <pre>{JSON.stringify(loadingControllaCodiceFiscale, null, 2)}</pre> */}
            <pre>{JSON.stringify(controlloCodiceFiscale, null, 2)}</pre> 
            {/* <pre>{JSON.stringify(deprules, null, 2)}</pre> */}
            {mostra && (
                <>
                    <FormGroup row>
                        <Box width={{xs: 1, md: 1, lg: 1/2}} 
                            mb={"1.2em"}>
                            
                            <BSSText
                                name={`datifatt.nome`} 
                                label={__t("nome")}    
                                methods={methods}     
                                ControllerProps={{
                                    rules: {
                                        required: __t("validazione.requiredField", ["nome"])
                                    }
                                }}                                                                            
                            />
                        </Box>  
                        <Box width={{xs: 1, md: 1, lg: 1/2}} 
                            mb={"1.2em"}>
                            
                            <BSSText
                                name={`datifatt.cognome`} 
                                label={__t("cognome")}    
                                methods={methods}     
                                ControllerProps={{
                                    rules: {
                                        required: __t("validazione.requiredField", ["cognome"]),
                                        
                                    }
                                }}                                                                            
                            />
                        </Box>                                                                                      
                    </FormGroup>

                    <FormGroup row>

                        <Box {...boxProps}>

                            <BSSText
                                name={`datifatt.codicefiscale`} 
                                label={`${__t("codicefiscale")}`}    
                                methods={methods}                                                                                                                                         
                                ControllerProps={{
                                    rules: {
                                        ...deprules,
                                        validate: () =>  {
                                            logger.debug("valido codice fiscale", controlloCodiceFiscale)
                                            return controlloCodiceFiscale.messaggio !== null || controlloCodiceFiscale.messaggio
                                        }
                                    }

                                }}                                                                            
                            />
                        </Box>
                    </FormGroup>

                    <FormGroup row>
                    <Box width={{xs: 1, md: 1, lg: 3/7}} 
                            mb={"1.2em"}>
                            
                            <BSSText
                                name={`datifatt.luogonascita`} 
                                label={__t("luogonascita")}    
                                methods={methods}     
                                TextFieldProps={{inputProps: {readOnly: true}}}  
                                ControllerProps={{
                                    rules: {
                                        required: __t("validazione.requiredField", ["luogonascita"])
                                    }
                                }}                                                                            
                            />
                        </Box>  
                        <Box width={{xs: 1, md: 1, lg: 3/7}} 
                            mb={"1.2em"}>
                            
                            <BSSText
                                name={`datifatt.datanascita`} 
                                label={__t("datanascita")}    
                                methods={methods}     
                                TextFieldProps={{inputProps: {readOnly: true}}}  
                                ControllerProps={{
                                    rules: {
                                        required: __t("validazione.requiredField", ["datanascita"])
                                    }
                                }}                                                                            
                            />
                        </Box>
                        <Box width={{xs: 1, md: 1, lg: 1/7}} 
                            mb={"1.2em"}>
                            
                            <BSSText
                                name={`datifatt.sesso`} 
                                label={__t("sesso")}    
                                methods={methods}     
                                TextFieldProps={{inputProps: {readOnly: true}}}  
                                ControllerProps={{
                                    rules: {
                                        required: __t("validazione.requiredField", ["sesso"])
                                    }
                                }}                                                                            
                            />
                        </Box>       
                    </FormGroup>
                </>
            )}
            
        </>
    )
    
}

const SimpleCodFisc = ({methods, boxProps={width: {xs: 1, md: 1, lg: 1}, mb: "1.2em"}}) => {

    return (
        <FormGroup row>

            <Box {...boxProps}>

                <BSSText
                    name={`datifatt.codicefiscale`} 
                    label={`${__t("codicefiscale")}`}    
                    methods={methods}                                                                        
                />
            </Box>
        </FormGroup>
    )
}

export default CodFisc;
export {SimpleCodFisc}